import { useState, useEffect, useCallback, useRef } from 'react'

export interface UseDropdownNavigationOptions<T extends { id: string | number }> {
  isOpen: boolean
  results: T[]
  hasViewAllButton?: boolean
  shouldShowViewAllButton?: boolean
  onDropdownOpenChange?: (open: boolean) => void
  onResultClick?: (result: T, index: number) => void
  onViewAllClick?: () => void
}

export function useDropdownNavigation<T extends { id: string | number }>({
  isOpen,
  results,
  hasViewAllButton = true,
  shouldShowViewAllButton = true,
  onDropdownOpenChange,
  onResultClick,
  onViewAllClick,
}: UseDropdownNavigationOptions<T>) {
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const resultItemRefs = useRef<(HTMLDivElement | null)[]>([])
  const viewAllButtonRef = useRef<HTMLButtonElement | null>(null)

  // Reset selected index when dropdown opens/closes or results change
  useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(-1)
    }
  }, [isOpen])

  // Reset refs array when results change
  useEffect(() => {
    resultItemRefs.current = resultItemRefs.current.slice(0, results.length)
  }, [results])

  const totalItems = results.length + (hasViewAllButton && shouldShowViewAllButton ? 1 : 0)

  const scrollSelectedItemIntoView = useCallback(
    (index: number) => {
      setTimeout(() => {
        if (index < results.length && resultItemRefs.current[index]) {
          resultItemRefs.current[index]?.scrollIntoView({
            block: 'nearest' as ScrollLogicalPosition,
            behavior: 'smooth',
          })
        } else if (index === results.length && viewAllButtonRef.current) {
          viewAllButtonRef.current.scrollIntoView({
            block: 'nearest' as ScrollLogicalPosition,
            behavior: 'smooth',
          })
        }
      }, 0)
    },
    [results.length]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return

      if (totalItems === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => {
            const nextIndex = prev < totalItems - 1 ? prev + 1 : 0
            scrollSelectedItemIntoView(nextIndex)
            return nextIndex
          })
          break

        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : totalItems - 1
            scrollSelectedItemIntoView(nextIndex)
            return nextIndex
          })
          break

        case 'Enter': {
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            onResultClick?.(results[selectedIndex], selectedIndex)
          } else if (selectedIndex === results.length && onViewAllClick) {
            onViewAllClick()
          }
          break
        }

        case 'Escape':
          onDropdownOpenChange?.(false)
          setSelectedIndex(-1)
          break
      }
    },
    [
      isOpen,
      totalItems,
      selectedIndex,
      results,
      onResultClick,
      onViewAllClick,
      onDropdownOpenChange,
      scrollSelectedItemIntoView,
    ]
  )

  const handleResultClick = useCallback(
    (result: T, index: number) => {
      onDropdownOpenChange?.(false)
      setSelectedIndex(-1)
      resultItemRefs.current = []
      onResultClick?.(result, index)
    },
    [onDropdownOpenChange, onResultClick]
  )

  const handleResultClickForDropdown = useCallback(
    (result: T) => {
      const index = results.findIndex((r) => r.id === result.id)
      handleResultClick(result, index)
    },
    [results, handleResultClick]
  )

  const handleViewAllClick = useCallback(() => {
    onDropdownOpenChange?.(false)
    setSelectedIndex(-1)
    resultItemRefs.current = []
    onViewAllClick?.()
  }, [onDropdownOpenChange, onViewAllClick])

  return {
    selectedIndex,
    resultItemRefs,
    viewAllButtonRef,
    handleKeyDown,
    handleResultClick,
    handleResultClickForDropdown,
    handleViewAllClick,
    totalItems,
  }
}
